import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FaBell, FaSearch, FaSignOutAlt, FaCog,
  FaChevronRight, FaTimes, FaBars,
  FaFileInvoice, FaArrowUp, FaArrowDown,
  FaTruck, FaChartBar, FaBullseye, FaTag,
  FaShieldAlt, FaDatabase, FaUsers,
  FaMoneyBillWave, FaHandshake, FaCashRegister,
  FaRobot
} from 'react-icons/fa';
import api from '../../services/api';

const BREADCRUMBS = {
  '/dashboard':             ['Dashboard'],
  '/comprobantes':          ['Finanzas', 'Comprobantes'],
  '/ingresos':              ['Finanzas', 'Ingresos'],
  '/gastos':                ['Finanzas', 'Gastos'],
  '/proveedores':           ['Finanzas', 'Proveedores'],
  '/reportes':              ['Finanzas', 'Reportes'],
  '/alertas':               ['Alertas'],
  '/asistente':             ['Asistente IA'],
  '/presupuestos':          ['Administración', 'Presupuestos'],
  '/categorias':            ['Administración', 'Categorías'],
  '/auditoria':             ['Administración', 'Auditoría'],
  '/usuarios':              ['Administración', 'Usuarios'],
  '/backup':                ['Administración', 'Backup'],
  '/balance-contador':      ['Contabilidad', 'Balance'],
  '/comprobantes-contador': ['Contabilidad', 'Comprobantes'],
  '/planillas/nomina':      ['Planillas', 'Nómina'],
  '/planillas/empleados':   ['Planillas', 'Empleados'],
  '/honorarios':            ['Planillas', 'Honorarios'],
  '/caja-chica':            ['Planillas', 'Caja Chica'],
};

const MODULOS = [
  { palabras:['dashboard','inicio','resumen'],         ruta:'/dashboard',             label:'Dashboard',            icono:<FaChartBar size={11} color="#3B82F6"/> },
  { palabras:['comprobante','factura','boleta'],        ruta:'/comprobantes',          label:'Comprobantes',         icono:<FaFileInvoice size={11} color="#8B5CF6"/> },
  { palabras:['ingreso','cobro','venta'],               ruta:'/ingresos',              label:'Ingresos',             icono:<FaArrowUp size={11} color="#22C55E"/> },
  { palabras:['gasto','egreso','pago'],                 ruta:'/gastos',                label:'Gastos',               icono:<FaArrowDown size={11} color="#EF4444"/> },
  { palabras:['proveedor','empresa','ruc'],             ruta:'/proveedores',           label:'Proveedores',          icono:<FaTruck size={11} color="#F59E0B"/> },
  { palabras:['reporte','informe','pdf'],               ruta:'/reportes',              label:'Reportes',             icono:<FaChartBar size={11} color="#06B6D4"/> },
  { palabras:['alerta','notificacion'],                 ruta:'/alertas',               label:'Alertas',              icono:<FaBell size={11} color="#F59E0B"/> },
  { palabras:['presupuesto','limite'],                  ruta:'/presupuestos',          label:'Presupuestos',         icono:<FaBullseye size={11} color="#EF4444"/> },
  { palabras:['categoria','clasificacion'],             ruta:'/categorias',            label:'Categorías',           icono:<FaTag size={11} color="#8B5CF6"/> },
  { palabras:['auditoria','historial','log'],           ruta:'/auditoria',             label:'Auditoría',            icono:<FaShieldAlt size={11} color="#374151"/> },
  { palabras:['usuario','cuenta','permiso','rol'],      ruta:'/usuarios',              label:'Usuarios',             icono:<FaUsers size={11} color="#3B82F6"/> },
  { palabras:['backup','respaldo','base de datos'],     ruta:'/backup',                label:'Backup',               icono:<FaDatabase size={11} color="#94A3B8"/> },
  { palabras:['nomina','sueldo','planilla'],            ruta:'/planillas/nomina',      label:'Nómina',               icono:<FaMoneyBillWave size={11} color="#22C55E"/> },
  { palabras:['empleado','trabajador','personal'],      ruta:'/planillas/empleados',   label:'Empleados',            icono:<FaUsers size={11} color="#8B5CF6"/> },
  { palabras:['honorario','recibo','4ta'],              ruta:'/honorarios',            label:'Honorarios',           icono:<FaHandshake size={11} color="#EF4444"/> },
  { palabras:['caja','fondo','efectivo'],               ruta:'/caja-chica',            label:'Caja Chica',           icono:<FaCashRegister size={11} color="#F59E0B"/> },
  { palabras:['asistente','ia','inteligencia'],         ruta:'/asistente',             label:'Asistente IA',         icono:<FaRobot size={11} color="#06B6D4"/> },
];

export default function Header({ alertasCount = 0, onToggleSidebar }) {
  const { usuario, logout } = useAuth();
  const loc      = useLocation();
  const navigate = useNavigate();

  const [menuOpen,     setMenuOpen]     = useState(false);
  const [busqueda,     setBusqueda]     = useState('');
  const [resultados,   setResultados]   = useState([]);
  const [buscando,     setBuscando]     = useState(false);
  const [mostrarDrop,  setMostrarDrop]  = useState(false);
  const [searchOpen,   setSearchOpen]   = useState(false);
  const busquedaRef = useRef(null);
  const timeoutRef  = useRef(null);

  const crumbs = BREADCRUMBS[loc.pathname] || [loc.pathname.split('/').pop()];

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const buscar = async (texto) => {
    if (!texto || texto.length < 2) { setResultados([]); setMostrarDrop(false); return; }
    setBuscando(true); setMostrarDrop(true);
    const textoLower = texto.toLowerCase();
    const modulosEncontrados = MODULOS.filter(m =>
      m.palabras.some(p => p.includes(textoLower) || textoLower.includes(p)) ||
      m.label.toLowerCase().includes(textoLower)
    ).slice(0, 5).map(m => ({ tipo:'modulo', texto:m.label, icono:m.icono, ruta:m.ruta }));

    try {
      const [comp, ing, gas] = await Promise.all([
        api.get('/comprobantes').catch(() => ({ data:{ data:[] } })),
        api.get('/ingresos').catch(() => ({ data:{ data:[] } })),
        api.get('/gastos').catch(() => ({ data:{ data:[] } })),
      ]);
      const datos = [
        ...(comp.data?.data||[]).filter(c => c.serie?.toLowerCase().includes(textoLower)||c.descripcion?.toLowerCase().includes(textoLower)).slice(0,2).map(c => ({ tipo:'dato', texto:`${c.serie}-${c.numero_comprobante}`, sub:`S/ ${parseFloat(c.monto_total).toFixed(2)}`, icono:<FaFileInvoice size={11} color="#8B5CF6"/>, ruta:'/comprobantes' })),
        ...(ing.data?.data||[]).filter(i => i.concepto?.toLowerCase().includes(textoLower)).slice(0,2).map(i => ({ tipo:'dato', texto:i.concepto, sub:`S/ ${parseFloat(i.monto).toFixed(2)}`, icono:<FaArrowUp size={11} color="#22C55E"/>, ruta:'/ingresos' })),
        ...(gas.data?.data||[]).filter(g => g.concepto?.toLowerCase().includes(textoLower)).slice(0,2).map(g => ({ tipo:'dato', texto:g.concepto, sub:`S/ ${parseFloat(g.monto).toFixed(2)}`, icono:<FaArrowDown size={11} color="#EF4444"/>, ruta:'/gastos' })),
      ];
      const todos = [];
      if (modulosEncontrados.length) { todos.push({ tipo:'titulo', texto:'📂 Módulos' }); todos.push(...modulosEncontrados); }
      if (datos.length) { todos.push({ tipo:'titulo', texto:'🔍 Resultados' }); todos.push(...datos); }
      setResultados(todos);
    } catch { setResultados(modulosEncontrados); }
    finally { setBuscando(false); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setBusqueda(val);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => buscar(val), 350);
  };

  const handleSeleccionar = (item) => {
    if (item.tipo === 'titulo') return;
    navigate(item.ruta);
    setBusqueda(''); setResultados([]); setMostrarDrop(false); setSearchOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (busquedaRef.current && !busquedaRef.current.contains(e.target)) {
        setMostrarDrop(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const iniciales = usuario
    ? `${usuario.nombre?.[0]||''}${usuario.apellido?.[0]||''}`.toUpperCase()
    : 'U';

  return (
    <header style={{
      height: 64, background: '#fff',
      borderBottom: '1px solid #E2E8F0',
      display: 'flex', alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 24px',
      position: 'sticky', top: 0, zIndex: 100,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      gap: 12, minWidth: 0,
    }}>

      {/* Izquierda — hamburger + breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0, flex:1 }}>
        {/* Botón hamburger — mobile */}
        <button
          onClick={onToggleSidebar}
          style={{
            background:'none', border:'none', cursor:'pointer',
            color:'#64748B', padding:6, borderRadius:6,
            display:'flex', alignItems:'center', justifyContent:'center',
            flexShrink:0,
          }}
        >
          <FaBars size={16} />
        </button>

        {/* Breadcrumb — se oculta en pantallas pequeñas con CSS */}
        <div className="header-breadcrumb" style={{
          display:'flex', alignItems:'center', gap:4,
          fontSize:13, color:'#94A3B8', minWidth:0, overflow:'hidden',
        }}>
          <span style={{ color:'#64748B', fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
            Inicio
          </span>
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              <FaChevronRight size={9} style={{ color:'#CBD5E1', flexShrink:0 }} />
              <span style={{
                color: i === crumbs.length-1 ? '#001B5C' : '#94A3B8',
                fontWeight: i === crumbs.length-1 ? 700 : 400,
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                maxWidth: 120,
              }}>
                {c}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Centro — buscador */}
      <div ref={busquedaRef} style={{ position:'relative', flex:'0 1 280px', minWidth:0 }}>
        {searchOpen ? (
          <div style={{ display:'flex', alignItems:'center', background:'#F8FAFC', borderRadius:10, padding:'6px 12px', gap:8, border:'1.5px solid #FF7A00' }}>
            <FaSearch size={12} color="#94A3B8" />
            <input
              type="text" autoFocus
              placeholder="Buscar módulos, comprobantes..."
              value={busqueda} onChange={handleChange}
              style={{ border:'none', background:'transparent', outline:'none', fontSize:12, width:'100%', color:'#0F172A', fontFamily:'Inter,sans-serif' }}
            />
            <button onClick={() => { setSearchOpen(false); setBusqueda(''); setResultados([]); setMostrarDrop(false); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#94A3B8', padding:0, display:'flex' }}>
              <FaTimes size={12} />
            </button>
          </div>
        ) : (
          <button onClick={() => setSearchOpen(true)} style={{
            display:'flex', alignItems:'center', gap:8,
            background:'#F8FAFC', border:'1.5px solid #E2E8F0',
            borderRadius:10, padding:'7px 14px', cursor:'pointer',
            fontSize:12, color:'#94A3B8', width:'100%',
            fontFamily:'Inter,sans-serif', transition:'all .2s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor='#FF7A00'}
            onMouseLeave={e => e.currentTarget.style.borderColor='#E2E8F0'}
          >
            <FaSearch size={12} />
            <span className="search-text">Buscar módulos...</span>
          </button>
        )}

        {/* Dropdown resultados */}
        {mostrarDrop && (
          <div style={{
            position:'absolute', top:44, left:0, right:0,
            background:'#fff', borderRadius:12,
            boxShadow:'0 8px 32px rgba(0,27,92,0.12)',
            zIndex:300, overflow:'hidden',
            maxHeight:380, overflowY:'auto',
            border:'1px solid #E2E8F0',
          }}>
            {resultados.length === 0 && !buscando ? (
              <div style={{ padding:16, fontSize:12, color:'#94A3B8', textAlign:'center' }}>
                Sin resultados para "{busqueda}"
              </div>
            ) : (
              resultados.map((r, i) => (
                r.tipo === 'titulo' ? (
                  <div key={i} style={{ padding:'6px 14px', fontSize:10, color:'#94A3B8', fontWeight:700, background:'#F8FAFC', textTransform:'uppercase', letterSpacing:1 }}>
                    {r.texto}
                  </div>
                ) : (
                  <div key={i} onClick={() => handleSeleccionar(r)}
                    style={{ padding:'9px 14px', cursor:'pointer', borderBottom:'1px solid #F1F5F9', display:'flex', alignItems:'center', gap:10, transition:'background .12s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <div style={{ width:26, height:26, borderRadius:6, background:'#F1F5F9', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {r.icono}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#0F172A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.texto}</div>
                      {r.sub && <div style={{ fontSize:11, color:'#94A3B8' }}>{r.sub}</div>}
                    </div>
                    {r.tipo === 'modulo' && <FaChevronRight size={9} color="#CBD5E1" style={{ marginLeft:'auto', flexShrink:0 }} />}
                  </div>
                )
              ))
            )}
          </div>
        )}
      </div>

      {/* Derecha — notificaciones + usuario */}
      <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>

        {/* Notificaciones */}
        <button onClick={() => navigate('/alertas')} style={{
          position:'relative', background:'none', border:'none',
          cursor:'pointer', width:36, height:36, borderRadius:8,
          display:'flex', alignItems:'center', justifyContent:'center',
          color:'#64748B', transition:'all .2s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background='#F1F5F9'; e.currentTarget.style.color='#001B5C'; }}
          onMouseLeave={e => { e.currentTarget.style.background='none'; e.currentTarget.style.color='#64748B'; }}
        >
          <FaBell size={17} />
          {alertasCount > 0 && (
            <span style={{
              position:'absolute', top:4, right:4,
              background:'#FF7A00', color:'#fff',
              borderRadius:'50%', fontSize:9, width:16, height:16,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:700,
            }}>
              {alertasCount > 9 ? '9+' : alertasCount}
            </span>
          )}
        </button>

        {/* Usuario */}
        <div style={{ position:'relative' }}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display:'flex', alignItems:'center', gap:8,
              background:'none', border:'1.5px solid #E2E8F0',
              borderRadius:10, padding:'6px 10px',
              cursor:'pointer', transition:'all .2s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor='#FF7A00'}
            onMouseLeave={e => e.currentTarget.style.borderColor='#E2E8F0'}
          >
            <div style={{
              width:30, height:30, borderRadius:'50%',
              background:'linear-gradient(135deg, #FF7A00, #FF9A3C)',
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'#fff', fontSize:11, fontWeight:800, flexShrink:0,
            }}>
              {iniciales}
            </div>
            <div className="user-info" style={{ textAlign:'left', lineHeight:1.2 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#0F172A', whiteSpace:'nowrap' }}>
                {usuario?.nombre}
              </div>
              <div style={{ fontSize:10, color:'#94A3B8', textTransform:'capitalize' }}>
                {usuario?.rol}
              </div>
            </div>
          </button>

          {menuOpen && (
            <div style={{
              position:'absolute', right:0, top:46,
              background:'#fff', borderRadius:12,
              boxShadow:'0 8px 32px rgba(0,27,92,0.12)',
              minWidth:200, zIndex:200, overflow:'hidden',
              border:'1px solid #E2E8F0',
            }}>
              <div style={{ padding:'14px 16px', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#0F172A' }}>
                  {usuario?.nombre} {usuario?.apellido}
                </div>
                <div style={{ fontSize:11, color:'#94A3B8' }}>{usuario?.email}</div>
              </div>
              <button onClick={() => { setMenuOpen(false); navigate('/usuarios'); }}
                style={{ width:'100%', padding:'11px 16px', border:'none', background:'transparent', textAlign:'left', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, color:'#374151', fontFamily:'Inter,sans-serif' }}
                onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
              >
                <FaCog size={13} color="#94A3B8" /> Configuración
              </button>
              <button onClick={handleLogout}
                style={{ width:'100%', padding:'11px 16px', border:'none', background:'transparent', textAlign:'left', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, color:'#EF4444', fontFamily:'Inter,sans-serif' }}
                onMouseEnter={e => e.currentTarget.style.background='#FEE2E2'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
              >
                <FaSignOutAlt size={13} /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}